/**
 * Parses a JSON string defensively: returns `undefined` for empty input or
 * any parse failure instead of throwing. Callers narrow the `unknown` result
 * themselves. Centralizes the `if (!raw) … try { JSON.parse } catch` pattern
 * that several stored-preference parsers repeated by hand.
 */
export function safeJsonParse(raw: string | null | undefined): unknown {
  if (!raw) return undefined
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

/**
 * Reads a value from localStorage, JSON-parses it defensively, and runs it
 * through the caller's validator. Returns `undefined` when the key is
 * missing, unparseable, or fails validation. SSR-safe (no `window`), and
 * tolerant of a throwing `localStorage` (e.g. `SecurityError` when storage
 * is blocked in a private/hardened browser) so callers can use it safely in
 * a `useState` initializer without crashing on mount.
 */
export function readValidatedStorage<T>(
  key: string,
  validate: (parsed: unknown) => T | undefined,
): T | undefined {
  if (typeof window === 'undefined') return undefined
  let raw: string | null
  try {
    raw = localStorage.getItem(key)
  } catch {
    return undefined
  }
  return validate(safeJsonParse(raw))
}

/**
 * Per-key listener registry backing the `useSyncExternalStore`-based
 * localStorage hooks. A write helper calls `notifyStorageKeyChange(key)`
 * after touching storage so every component subscribed to that key via
 * `subscribeToStorageKey` re-reads and stays in sync — including two
 * components sharing a `storageKey` across different hooks.
 */
const storageKeyListeners = new Map<string, Set<() => void>>()

export function subscribeToStorageKey(
  key: string,
  onChange: () => void,
): () => void {
  let listeners = storageKeyListeners.get(key)
  if (!listeners) {
    listeners = new Set()
    storageKeyListeners.set(key, listeners)
  }
  listeners.add(onChange)
  return () => listeners?.delete(onChange)
}

export function notifyStorageKeyChange(key: string): void {
  storageKeyListeners.get(key)?.forEach((listener) => listener())
}
